import { defineStore } from 'pinia';
import plans from '../service/plans';
import seedStore from './seed';
import supplyStore from './supply';
import productStore from './product';
import useConfiguration from './configuration';
import BigNumber from 'bignumber.js';
import iconsService from '../service/icons';
import utils from '../service/utils';

const planStore = defineStore('plan', {
  // arrow function recommended for full type inference
  state: () => {
    return {
      plans: [],
      sortAscending: false,
      loading: false,
      currentPlanData: {
        totalSize: 0,
        season: null,
        divisions: [],
      },
    };
  },
  actions: {
    async updateCurrentPlanData(data, field) {
      if (field === 'totalSize') {
        this.currentPlanData.totalSize = data;
      } else if (field === 'divisions') {
        this.currentPlanData.divisions = data;
      } else if (field === 'selectProduct') {
        // Calculate seeds here
        if (!this.currentPlanData.season) {
          // If season is not selected, then select it based on product selection
          for (const [key, value] of Object.entries(data.product.season)) {
            if (value) {
              this.currentPlanData.season = key;
            }
          }
        }

        this.loading = true;

        const { divisionId, product, size } = data;
        let division = this.findDivisionById(divisionId);

        const useConfig = useConfiguration();

        division.seeds = await this.getSeedsAndCalculationsFromSeasonAndProduct(this.currentPlanData.season, product.id, size, useConfig.configuration.selectedGasoil);

        this.loading = false;
      } else if (field === 'deleteDivision') {
        const { id } = data;
        this.currentPlanData.divisions = this.currentPlanData.divisions.filter((d) => d.id != id);
      } else if (field === 'season') {
        this.currentPlanData.season = data.season;
      } else if (field === 'returnToDefault') {
        this.currentPlanData = {
          totalSize: 0,
          season: null,
          divisions: [],
        };
      } else if (field === 'getById') {
        delete data.__v;
        delete data.createdAt;
        delete data.updatedAt;

        for (let division of data.divisions) {
          division.widthInPercentage = this.getWidthInPercentage(data.totalSize, division.sizeInHectares);
          if (division.product) {
            division.product.icon = iconsService.getIcon(division.product.name);
          }
        }
        this.currentPlanData = data;
      } else if (field === 'changeSeasonWithProductSelected') {
        const { season } = this.currentPlanData;
        let newSeason;
        if (season === 'summer') {
          newSeason = 'winter';
        } else newSeason = 'summer';

        this.currentPlanData.season = newSeason;
        this.currentPlanData.divisions = [
          {
            id: 1,
            sizeInHectares: this.currentPlanData.totalSize,
            widthInPercentage: 100,
            product: null,
            seeds: [],
          },
        ];
      }
    },
    getWidthInPercentage(totalSize, sizeInHectares) {
      return 100 / (totalSize / sizeInHectares);
    },
    getSizeInHectaresByPercentage(widthInPercentage) {
      return Number(Math.round((this.currentPlanData.totalSize * widthInPercentage) / 100));
    },
    replaceDivision(i, data) {
      let clone = [...this.storage];
      clone[i] = data;
      this.storage = [...clone];
    },
    findDivisionById(id) {
      for (let division of this.currentPlanData.divisions) {
        if (division.id === id) {
          return division;
        }
      }
    },
    async getSeedsAndCalculationsFromSeasonAndProduct(season, productId, divisionSize, gasoilId) {

      const seed_store = seedStore();

      const dataToBeRenderedInTable = await Promise.all(
        seed_store.allSeeds
          .filter((seed) => {

            const seedIsAssociatedToThisProduct = seed?.product?._id === productId

            const seedIsFromTheSelectedSeason = seed.season[season] == true

            return seedIsAssociatedToThisProduct && seedIsFromTheSelectedSeason && seed.isAvailable

          })
          .map((seed) => this.getCalculations(seed._id, divisionSize, gasoilId, productId))
      );

      // Sort by profit, lower to higher
      return dataToBeRenderedInTable.sort((a, b) => a.calculationsData.profit - b.calculationsData.profit);
    },

    async getCalculations(seedId, divisionSize, gasoilId, productId) {
      if (!divisionSize || !seedId) return;
      const seed_store = seedStore();
      const supply_store = supplyStore();

      const res = await seed_store.getSeedById(seedId);
      const seed = res.result;
      if (!seed) return;

      const gasoil = await supply_store.getSuppliesById(gasoilId);

      const bagPerHec = seed.bagsPerHectare.recommended;

      const performancePerHectare = seed.performancePerHectare.estimate;

      const bagsNeeded = this.calculate({ field: 'bagsNeeded', bagPerHec, divisionSize });

      const pricePerBagPerAmount = this.getPricePerBagPerAmount(bagsNeeded, seed.pricePerBag);

      const bagsCost = this.calculate({ field: 'bagsCost', bagsNeeded, pricePerBagPerAmount });

      const estimatedHarvestInTons = this.calculate({ field: 'estimatedHarvestInTons', performancePerHectare, divisionSize });

      const estimatedHarvestInQuintals = this.calculate({ field: 'estimatedHarvestInQuintals', estimatedHarvestInTons });

      const seedMarketValue = await this.getProductPrice(productId);

      const estimatedHarvestValue = this.calculate({
        field: 'estimatedHarvestValue',
        estimatedHarvestInTons,
        seedMarketValue,
      });

      const gasoilPricePerFormat = gasoil.price.perFormat;
      tota
      const gasoilCost = this.calculate({
        field: 'gasoilCost',
        gasoilDose: seed.gasoil.recommended,
        gasoilPricePerFormat,
        divisionSize
      });

      // Before processing, each seed has a set of supplies that we gotta reuse when the user selects another supply
      const availableSupplies = seed.supplies.filter((supply) => supply.supply.isAvailable);

      const { supplyCostObject, suppliesWithCalculations, additiveOptions, fertilizerOptions, phytosanitaryOptions, fertilizerAndPhytosanitaryOptions } =
        this.processSupplies(availableSupplies, divisionSize);

      const totalCost = this.calculate({
        field: 'totalCost',
        bagsCost,
        gasoilCost,
        totalSupplyCost: supplyCostObject.totalSupplyCost,
      });

      const profit = this.calculate({
        field: 'profit',
        estimatedHarvestValue,
        totalCost,
      });

      const suppliesToggle = seed.suppliesToggle ? seed.suppliesToggle : true;
      const gasoilToggle = seed.gasoilToggle ? seed.gasoilToggle : true;

      return {
        additiveOptions, // Options for select input
        // availableSupplies, // Array of unprocessed supplies that are available
        calculationsData: {
          bagsNeeded,
          bagsCost,
          estimatedHarvestInQuintals,
          estimatedHarvestInTons,
          estimatedHarvestValue,
          gasoilCost,
          gasoilPricePerFormat,
          pricePerBagPerAmount,
          profit,
          seedMarketValue,
          supplyCostObject, // It has the currently selected supplies and the totalSupplyCost
          totalCost,
          suppliesToggle,
          gasoilToggle
        },
        fertilizerAndPhytosanitaryOptions, // Options for select input
        fertilizerOptions, // Options for select input
        phytosanitaryOptions, // Options for select input
        seed, // Original seed we get from the backend. We use this to get the original seed when changing dose, then that new recalculated object replaces the one stored in suppliesWithCalculations
        suppliesWithCalculations, // Array of supplies for the aformentioned seed with data already calculated for it. We use this to add supplies in the seedTable so it must NOT be edited
      };
    },

    calculate({
      field,
      operation,
      bagsCost,
      bagsNeeded,
      bagPerHec,
      divisionSize,
      estimatedHarvestInTons,
      estimatedHarvestValue,
      formatAmount,
      gasoilCost,
      gasoilPricePerFormat,
      gasoilDose,
      performancePerHectare,
      pricePerBagPerAmount,
      pricePerPresentation,
      presentationAmount,
      seedMarketValue,
      suppliesArray,
      supplyDose,
      totalAmountInFormatUnit,
      totalCost,
      totalSupplyCost,
      supplyCost,
    }) {
      if (!field) {
        switch (operation) {
          // operations are for more complex use of our equations
          case 'removeSupplyFromTotalSupplyCost':
            return BigNumber(totalSupplyCost).minus(BigNumber(supplyCost)).toNumber();

          case 'addSupplytoTotalSupplyCost':
            return BigNumber(totalSupplyCost).plus(BigNumber(supplyCost)).toNumber();
          default:
            break;
        }
      }

      switch (field) {
        // case 'gasoilPricePerFormat':
        // case 'pricePerBagPerAmount':
        // case 'seedMarketValue':
        // ^^ these come from other methods, they can just be passed down as parameters
        case 'bagsNeeded':
          return BigNumber(bagPerHec).times(BigNumber(divisionSize)).integerValue(BigNumber.ROUND_CEIL).toNumber();

        case 'bagsCost':
          return BigNumber(bagsNeeded).times(BigNumber(pricePerBagPerAmount)).toNumber();

        case 'estimatedHarvestInTons':
          return BigNumber(performancePerHectare).times(BigNumber(divisionSize)).dividedBy(BigNumber(1000)).toNumber();

        case 'estimatedHarvestInQuintals':
          return BigNumber(estimatedHarvestInTons).times(BigNumber(10)).toNumber();

        case 'estimatedHarvestValue':
          return BigNumber(estimatedHarvestInTons).times(BigNumber(seedMarketValue)).toNumber();

        case 'gasoilCost':
          return BigNumber(gasoilDose).times(BigNumber(gasoilPricePerFormat)).times((divisionSize)).toNumber();

        case 'formatAmount':
          return BigNumber(supplyDose).times(BigNumber(divisionSize)).toNumber();

        case 'presentationAmount':
          return BigNumber(formatAmount).dividedBy(BigNumber(totalAmountInFormatUnit)).integerValue(BigNumber.ROUND_CEIL).toNumber();

        case 'supplyCost':
          return BigNumber(presentationAmount).times(BigNumber(pricePerPresentation)).toNumber();

        case 'totalSupplyCost': {
          let totalSupplyCost = 0;

          suppliesArray.forEach((supply) => (totalSupplyCost = BigNumber(totalSupplyCost).plus(BigNumber(supply.cost).toNumber())));

          if (totalSupplyCost > 0) {
            totalSupplyCost = totalSupplyCost.toNumber();
          }

          return totalSupplyCost;
        }

        case 'totalCost':
          return BigNumber(bagsCost).plus(BigNumber(gasoilCost)).plus(BigNumber(totalSupplyCost)).toNumber();

        case 'profit':
        default:
          return BigNumber(estimatedHarvestValue).minus(BigNumber(totalCost)).toNumber();
      }
    },

    /**
     *
     * @param {object} supply - Supply that comes from db or seed object, untouched
     * @param {*} divisionSize - Current division size
     * @returns processedSupply
     */
    processSupply(supply, divisionSize, userSelectedDose) {
      const {
        isDefault,
        supply: { name, presentation, type, format, price, _id },
        dose,
      } = supply;


      const parsedDose = dose.value || dose.value == '' ? dose.value : dose.recommended

      const formatAmount = this.calculate({
        field: 'formatAmount',
        supplyDose: utils.replaceCommaWithDot(parsedDose),
        divisionSize,
      });

      const presentationAmount = this.calculate({
        field: 'presentationAmount',
        formatAmount,
        totalAmountInFormatUnit: presentation.totalAmountInFormatUnit,
      });

      const cost = utils.formatInputNumber(this.calculate({
        field: 'supplyCost',
        presentationAmount,
        pricePerPresentation: price.perPresentation,
      }))

      dose.value = parsedDose

      return {
        name,
        dose,
        description: presentation.description,
        type,
        formatAmount,
        format,
        isDefault: !!isDefault,
        pricePerFormat: price.perFormat,
        presentationAmount,
        totalAmountInFormatUnit: presentation.totalAmountInFormatUnit,
        cost,
        id: _id,
      };
    },

    /**
     * Takes all available supplies, process each one, adding calculations to them
     * @param {array} supplies Receives an array of supplies that are available
     */
    processSupplies(supplies, divisionSize) {
      const suppliesWithCalculations = supplies.map((supply) => this.processSupply({ ...supply, isDefault: supply.isDefault }, divisionSize));

      const defaultSupplies = suppliesWithCalculations.filter((s) => s.isDefault);
      const [selectedAdditiveSupplies, selectedFertilizerSupplies, selectedPhytosanitarySupplies, selectedFertilizerAndPhytosanitarySupplies] =
        this.getSelectedSuppliesByDefault(defaultSupplies);
      const suppliesSelectedByDefault = [
        ...selectedAdditiveSupplies,
        ...selectedFertilizerSupplies,
        ...selectedPhytosanitarySupplies,
        ...selectedFertilizerAndPhytosanitarySupplies,
      ];
      const totalSupplyCost = this.calculate({ field: 'totalSupplyCost', suppliesArray: suppliesSelectedByDefault });
      const supplyCostObject = {
        selectedFertilizerSupplies,
        selectedPhytosanitarySupplies,
        selectedAdditiveSupplies,
        selectedFertilizerAndPhytosanitarySupplies,
        totalSupplyCost,
      };

      const additiveOptions = this.getSuppliesOptions(supplies, ['additive']);
      const fertilizerOptions = this.getSuppliesOptions(supplies, ['fertilizer']);
      const phytosanitaryOptions = this.getSuppliesOptions(supplies, ['phytosanitary']);
      const fertilizerAndPhytosanitaryOptions = this.getSuppliesOptions(supplies, ['fertilizerAndPhytosanitary']);
      return {
        supplyCostObject,
        additiveOptions,
        fertilizerOptions,
        phytosanitaryOptions,
        fertilizerAndPhytosanitaryOptions,
        suppliesWithCalculations,
      };

    },

    getSelectedSuppliesByDefault(supplies) {
      const selectedAdditiveSupplies = [];
      let selectedFertilizerSupplies = [];
      let selectedPhytosanitarySupplies = [];
      const selectedFertilizerAndPhytosanitarySupplies = [];

      for (let supply of supplies) {
        switch (supply.type) {
          case 'fertilizerAndPhytosanitary': {
            selectedFertilizerAndPhytosanitarySupplies.push(supply);
            break;
          }
          case 'fertilizer':
            selectedFertilizerSupplies.push(supply);
            break;

          case 'phytosanitary':
            selectedPhytosanitarySupplies.push(supply);
            break;

          case 'additive':
          default:
            selectedAdditiveSupplies.push(supply);
        }
      }

      //if (selectedFertilizerAndPhytosanitarySupplies.length) {
      //  selectedFertilizerSupplies = [];
      //  selectedPhytosanitarySupplies = [];
      //}

      return [selectedAdditiveSupplies, selectedFertilizerSupplies, selectedPhytosanitarySupplies, selectedFertilizerAndPhytosanitarySupplies];
    },

    getSuppliesOptions(supplies, types) {
      let container = [];
      for (let supply of supplies) {
        const { name, _id, type } = supply.supply;
        if (types.includes(type)) {
          container.push({
            label: name,
            value: _id,
          });
        }
      }
      return container;
    },

    getPricePerBagPerAmount(amountOfBags, pricePerBagArray) {
      let priceToBeReturned;
      for (let i = 0; i < pricePerBagArray.length; i++) {
        if (amountOfBags > pricePerBagArray[i].from && amountOfBags <= pricePerBagArray[i].to) {
          priceToBeReturned = pricePerBagArray[i].price;
          break;
        } else {
          priceToBeReturned = pricePerBagArray[pricePerBagArray.length - 1].price;
          break;
        }
      }
      return priceToBeReturned;
    },

    async getProductPrice(productId) {
      const product_store = productStore();
      let product = await product_store.getProductsById(productId);
      return product.price.price;
    },
    async getPlans() {
      try {
        this.loading = true;
        this.plans = [];
        const res = await plans.getPlans();
        this.plans = await res.result;
        this.loading = false;
        return res.result;
      } catch (e) {
        console.log(e);
        throw e;
      }
    },

    async getById(id) {
      try {
        const res = await plans.getById(id);
        return res.result;
      } catch (e) {
        console.log(e);
        throw e;
      }
    },

    async createPlan() {
      try {
        for (let division of this.currentPlanData.divisions) {
          let productId = '';
          if (division.product) {
            productId = division.product._id;
            division.product = productId;
          }
          delete division.widthInPercentage;
        }
        const res = await plans.createPlan(this.currentPlanData);
        return res.result;
      } catch (e) {
        console.log(e);
        throw e;
      }
    },
    async removePlan(id) {
      try {
        await plans.removePlan(id);
        this.plans = this.plans.filter((p) => p._id !== id);
      } catch (e) {
        console.log(e);
        throw e;
      }
    },
    async updatePlan(id) {
      try {
        await plans.updatePlan(id, this.currentPlanData);
      } catch (e) {
        console.log(e);
        throw e;
      }
    },
    sortPlans(key) {
      this.sortAscending = !this.sortAscending;
      let clone = [...this.plans];
      if (key === 'name' || key === 'season') {
        clone.sort(utils.alphabeticSorting(key, this.sortAscending));
      } else if (key === 'totalSize') {
        clone.sort(utils.numericalSorting(key, this.sortAscending));
      }
      this.plans = clone;
    },
  },
});

export default planStore;
